import posthog from 'posthog-js'

/**
 * Value written in place of sensitive property values when search log encryption is on.
 * @type {string}
 */
export const REDACTED_VALUE = '-REDACTED-'

/**
 * Property keys whose values are replaced with {@link REDACTED_VALUE}
 * when search log encryption is on.
 * @type {readonly string[]}
 */
export const REDACTED_PROPERTY_KEYS = Object.freeze([
  'search_query',
  'search_keyword',
  'search_term',
  'search_terms'
])

const REDACTED_KEYS_SET = new Set(REDACTED_PROPERTY_KEYS)

/**
 * @param {Record<string, unknown>} source
 * @returns {Record<string, unknown>}
 */
function applySearchLogRedaction(source) {
  const result = { ...source }
  for (const key of Object.keys(result)) {
    if (key.startsWith('$')) {
      continue
    }
    if (REDACTED_KEYS_SET.has(key)) {
      result[key] = REDACTED_VALUE
    }
  }
  return result
}

export default function({ env, app, store }, inject) {
  // Init PostHog
  posthog.init(env.POSTHOG_API_KEY, {
    api_host: env.POSTHOG_API_HOST,
    autocapture: true,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    persistence: 'localStorage+cookie'
  })

  // Wrap the capture method to apply redaction logic for properties related to search keyword/terms and search query
  // Store reference is captured in closure - will be accessed at runtime when capture is called
  const originalCapture = posthog.capture.bind(posthog)
  posthog.capture = function(eventName, properties = {}) {
    // Don't redact PostHog's internal identify events
    // These events are used to set person properties and should not be redacted
    if (eventName === '$identify' || eventName === '$identify_feature_flags') {
      return originalCapture(eventName, properties)
    }

    // Get search_log_encryption from store at runtime (when capture is actually called)
    // By this time, the store should be populated with userSubscriptions data
    // Try multiple ways to access the store to ensure we can get it
    let currentStore = store

    // On client side, try to get store from app context if available
    if (typeof window !== 'undefined') {
      if (app && app.$store) {
        currentStore = app.$store
      } else if (app && app.store) {
        currentStore = app.store
      }
    }

    const features = currentStore?.state?.userSubscriptions?.features
    const hasSearchLogEncryption = !!features?.search_log_encryption

    const clonedAttributes = JSON.parse(JSON.stringify(properties))
    const extraAttributesToUse =
      hasSearchLogEncryption && properties
        ? applySearchLogRedaction(clonedAttributes)
        : clonedAttributes

    // Call the original capture method with redacted properties
    return originalCapture(eventName, extraAttributesToUse)
  }

  // Inject PostHog into the application and make it available via this.$posthog (or app.$posthog)
  inject('posthog', posthog)
}
